import EditTaskModal from "../components/EditTaskModal";
import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import { collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import { FaClipboardCheck, FaEdit, FaTrash, FaPlus, FaInfoCircle, FaExternalLinkAlt } from "react-icons/fa";
import { FiFilter, FiCheckSquare, FiClock, FiAlertCircle, FiPauseCircle, FiList } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { notifyUser } from "../utils/notifications";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import "./Tasks.css";

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    ['clean'],
  ],
};
const QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'color', 'background', 'list', 'bullet', 'indent',
];

const stripHtml = (html) => html?.replace(/<[^>]*>/g, '') || '';

const Tasks = ({ selectedEmployee }) => {
  const { userData } = useAuth();
  const navigate = useNavigate();
  const isManagementView = !!selectedEmployee;
  const userInFocus = isManagementView ? selectedEmployee : userData;

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTask, setNewTask] = useState({ title: "", description: "", status: "Pending", dueDate: "", priority: "Normal" });
  const [editingTask, setEditingTask] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: "", type: "" });
  const [filterStatus, setFilterStatus] = useState("All");
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);


  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: "", type: "" }), 3000);
  };

  const hasManagementPermission = isManagementView && (
    userData.role === 'superadmin' || (userData.role === 'admin' && userInFocus.role === 'employee')
  );

  useEffect(() => {
    if (!userInFocus?.uid) return;
    setLoading(true);
    const q = query(collection(db, "tasks"), where("uid", "==", userInFocus.uid));
    const unsub = onSnapshot(q, (snap) => {
      const fetched = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setTasks(fetched.sort((a, b) => {
        const aTime = a.assignedAt?.toDate ? a.assignedAt.toDate().getTime()
          : (a.assignedAt ? new Date(a.assignedAt).getTime() : 0);
        const bTime = b.assignedAt?.toDate ? b.assignedAt.toDate().getTime()
          : (b.assignedAt ? new Date(b.assignedAt).getTime() : 0);
        return bTime - aTime; // newest first
      }));
      setLoading(false);
    }, () => {
      showNotification("Could not fetch tasks.", "error");
      setLoading(false);
    });
    return () => unsub();
  }, [userInFocus?.uid]);

  const handleAddTask = async () => {
    if (!newTask.title || !newTask.dueDate) return showNotification("Title and Due Date are required.", "error");
    setIsSubmitting(true);
    try {
      const now = new Date();
      // Use IST for ticket number timestamp
      const istParts = new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).formatToParts(now);
      const gp = (type) => istParts.find(p => p.type === type)?.value || '00';
      const createdDate = `${gp('year')}${gp('month')}${gp('day')}${gp('hour')}${gp('minute')}${gp('second')}`;
      const empId = userInFocus.employeeId || userInFocus.uid;
      const ticketNumber = `TASK-${empId}-${createdDate}`;
      
      await addDoc(collection(db, "tasks"), {
        ...newTask,
        uid: userInFocus.uid,
        assignedToName: userInFocus.name || "",
        assignedToEmail: userInFocus.email || "",
        assignedBy: userData.uid,
        assignedByName: userData.name || "Admin",
        assignedAt: serverTimestamp(),
        completedAt: null,
        ticketNumber
      });

      // ── Notify the employee that a task was assigned ──
      if (userInFocus.uid !== userData.uid) {
        notifyUser({
          recipientUid: userInFocus.uid,
          type: 'task_assigned',
          title: 'New Task Assigned',
          body: `${userData?.name || 'Admin'} assigned you: "${newTask.title}" (${newTask.priority} priority, due ${newTask.dueDate})`,
          link: '/dashboard',
          senderUid: userData.uid,
          senderName: userData.name || 'Admin',
          metadata: { ticketNumber, priority: newTask.priority },
        });
      }

      setNewTask({ title: "", description: "", status: "Pending", dueDate: "", priority: "Normal" });
      showNotification(`Task assigned! Ticket: ${ticketNumber}`);
      setIsCreateFormOpen(false);
    } catch (err) {
      showNotification("Failed to assign task.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateTaskStatus = async (taskId, newStatus) => {
    // In management view only authorised managers may change status
    if (isManagementView && !hasManagementPermission) {
      showNotification("You don't have permission to update this task.", "error");
      return;
    }
    const taskToUpdate = tasks.find(t => t.id === taskId);
    if (!taskToUpdate || taskToUpdate.status === newStatus) return;

    const allowedTransitions = {
      'Pending': ['In Progress', 'On Hold'],
      'In Progress': ['Completed', 'On Hold'],
      'On Hold': ['In Progress', 'Completed'],
      'Completed': []
    };

    if (userData.role !== 'superadmin' && !allowedTransitions[taskToUpdate.status].includes(newStatus)) {
      showNotification(`Invalid transition from ${taskToUpdate.status}`, "error");
      return;
    }

    try {
      await updateDoc(doc(db, "tasks", taskId), {
        status: newStatus,
        completedAt: newStatus === 'Completed' ? serverTimestamp() : null
      });
      showNotification(`Status updated to ${newStatus}`);

      // ── Notify the admin who assigned the task when an employee updates it ──
      if (
        taskToUpdate.assignedBy &&
        taskToUpdate.assignedBy !== userData?.uid
      ) {
        notifyUser({
          recipientUid: taskToUpdate.assignedBy,
          type: 'task_updated',
          title: 'Task Status Updated',
          body: `"${taskToUpdate.title}" moved to ${newStatus} by ${userData?.name || 'employee'}.`,
          link: '/dashboard/manage',
          senderUid: userData?.uid,
          senderName: userData?.name || '',
          metadata: { taskId, ticketNumber: taskToUpdate.ticketNumber, newStatus },
        });
      }
    } catch (err) {
      showNotification("Update failed.", "error");
    }
  };

  const filteredTasks = tasks.filter(t => filterStatus === "All" || t.status === filterStatus);

  const statusCounts = {
    All: tasks.length,
    Pending: tasks.filter(t => t.status === "Pending").length,
    "In Progress": tasks.filter(t => t.status === "In Progress").length,
    "On Hold": tasks.filter(t => t.status === "On Hold").length,
    Completed: tasks.filter(t => t.status === "Completed").length,
  };

  return (
    <div className="tasks-shell">
      {/* ── Toast Notification ── */}
      {notification.show && (
        <div className={`tasks-notification tasks-notification--${notification.type}`}>
          {notification.message}
        </div>
      )}

      {/* ══ Page Header ══════════════════════════════════════════════ */}
      <div className="tasks-page-header">
        <div className="tasks-page-header-left">
          <span className="tasks-header-icon-wrap"><FiCheckSquare size={20} /></span>
          <div>
            <h3 className="tasks-page-title">
              {isManagementView ? `Tasks — ${userInFocus.name}` : "My Tasks Board"}
            </h3>
            <p className="tasks-page-subtitle">
              {isManagementView
                ? `${userInFocus.role === "admin" ? "Admin" : "Employee"} · ${userInFocus.department || userInFocus.email || ""}`
                : "Track your assigned work items"}
            </p>
          </div>
        </div>
        <div className="tasks-header-right">
          {hasManagementPermission && (
            <button
              onClick={() => { setIsCreateFormOpen(!isCreateFormOpen); }}
              className={`tasks-create-btn ${isCreateFormOpen ? "tasks-create-btn--active" : ""}`}
            >
              <FaPlus size={12} />
              {isCreateFormOpen ? "Close Form" : "New Task"}
            </button>
          )}
        </div>
      </div>

      {/* ══ Summary Stat Pills ════════════════════════════════════════ */}
      <div className="tasks-stat-row">
        {[
          { key: "All",        label: "All",         icon: <FiList size={13} />,         cls: "stat--all" },
          { key: "Pending",    label: "Pending",      icon: <FiClock size={13} />,        cls: "stat--pending" },
          { key: "In Progress",label: "In Progress",  icon: <FiAlertCircle size={13} />,  cls: "stat--inprogress" },
          { key: "On Hold",    label: "On Hold",      icon: <FiPauseCircle size={13} />,  cls: "stat--onhold" },
          { key: "Completed",  label: "Completed",    icon: <FiCheckSquare size={13} />,  cls: "stat--completed" },
        ].map(s => (
          <button
            key={s.key}
            className={`tasks-stat-pill ${s.cls} ${filterStatus === s.key ? "tasks-stat-pill--active" : ""}`}
            onClick={() => setFilterStatus(s.key)}
          >
            {s.icon}
            <span className="stat-label">{s.label}</span>
            <span className="stat-count">{statusCounts[s.key]}</span>
          </button>
        ))}
      </div>

      {/* ══ Create Task Form ═════════════════════════════════════════ */}
      {isCreateFormOpen && (
        <div className="tasks-form-card">
          <div className="tasks-form-card-header">
            <FaPlus size={13} />
            <span>Assign New Work to {userInFocus.name}</span>
          </div>
          <div className="tasks-form-grid">
            <div className="tasks-field">
              <label className="tasks-field-label">Task Title <span className="tasks-field-required">*</span></label>
              <input
                className="tasks-input"
                placeholder="e.g., Design landing page"
                value={newTask.title}
                onChange={e => setNewTask({ ...newTask, title: e.target.value })}
              />
            </div>
            <div className="tasks-field">
              <label className="tasks-field-label">Due Date <span className="tasks-field-required">*</span></label>
              <input
                className="tasks-input"
                type="date"
                value={newTask.dueDate}
                onChange={e => setNewTask({ ...newTask, dueDate: e.target.value })}
              />
            </div>
            <div className="tasks-field">
              <label className="tasks-field-label">Priority</label>
              <select
                className="tasks-input tasks-select"
                value={newTask.priority}
                onChange={e => setNewTask({ ...newTask, priority: e.target.value })}
              >
                <option value="Normal">Normal</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
              </select>
            </div>
            <div className="tasks-field tasks-field--full">
              <label className="tasks-field-label">Instructions / Description</label>
              <ReactQuill
                className="tasks-quill"
                theme="snow"
                value={newTask.description}
                onChange={val => setNewTask({ ...newTask, description: val })}
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
                placeholder="Technical instructions, acceptance criteria…"
              />
            </div>
          </div>
          <div className="tasks-form-actions">
            <button onClick={handleAddTask} className="tasks-btn-primary" disabled={isSubmitting}>
              <FaPlus size={11} /> {isSubmitting ? "Assigning…" : "Assign Task"}
            </button>
            <button onClick={() => setIsCreateFormOpen(false)} className="tasks-btn-secondary">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ══ Tasks Table ══════════════════════════════════════════════ */}
      {loading ? (
        <div className="tasks-loading-state">
          <FiCheckSquare size={32} />
          <p>Loading tasks…</p>
        </div>
      ) : filteredTasks.length === 0 ? (
        <div className="tasks-empty-state">
          <FiCheckSquare size={40} />
          <h4>No tasks {filterStatus !== "All" ? `with status "${filterStatus}"` : "assigned yet"}</h4>
          <p>
            {hasManagementPermission
              ? "Use the \"New Task\" button above to assign work."
              : "Your manager hasn't assigned any tasks yet."}
          </p>
        </div>
      ) : (
        <div className="tasks-table-card">
          <div className="tasks-table-card-header">
            <FiList size={14} />
            <span>
              {filterStatus === "All" ? "All Tasks" : filterStatus}
            </span>
            <span className="tasks-table-count">{filteredTasks.length}</span>
          </div>
          <div className="tasks-table-wrapper">
            <table className="tasks-table">
              <thead>
                <tr>
                  <th className="tasks-th">Ticket ID</th>
                  <th className="tasks-th">Summary</th>
                  <th className="tasks-th">Priority</th>
                  <th className="tasks-th">Created</th>
                  <th className="tasks-th">Due Date</th>
                  <th className="tasks-th">Status</th>
                  <th className="tasks-th">Update</th>
                  {hasManagementPermission && <th className="tasks-th">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {filteredTasks.map(task => (
                  <tr key={task.id} className="tasks-tr">
                    <td className="tasks-td">
                      <span
                        className="tasks-ticket-id"
                        onClick={() => navigate(`/dashboard/task/${task.ticketNumber}`)}
                        title="Open ticket"
                      >
                        {task.ticketNumber} <FaExternalLinkAlt size={9} />
                      </span>
                    </td>
                    <td className="tasks-td">
                      <div className="tasks-summary">
                        <strong>{task.title}</strong>
                        {task.description && (() => {
                          const plain = stripHtml(task.description);
                          return <span>{plain.substring(0, 60)}{plain.length > 60 ? '…' : ''}</span>;
                        })()}
                        {task.assignedByName && (
                          <span className="tasks-assigned-by">↳ {task.assignedByName}</span>
                        )}
                      </div>
                    </td>
                    <td className="tasks-td">
                      <span className={`tasks-prio-badge tasks-prio--${task.priority?.toLowerCase() || "normal"}`}>
                        {task.priority || "Normal"}
                      </span>
                    </td>
                    <td className="tasks-td tasks-td--date">
                      {task.assignedAt
                        ? (typeof task.assignedAt.toDate === "function"
                            ? task.assignedAt.toDate().toLocaleDateString("en-IN", { timeZone: 'Asia/Kolkata', day: "2-digit", month: "short", year: "numeric" })
                            : new Date(task.assignedAt).toLocaleDateString("en-IN", { timeZone: 'Asia/Kolkata', day: "2-digit", month: "short", year: "numeric" }))
                        : "—"}
                    </td>
                    <td className="tasks-td tasks-td--date">
                      <span className={`tasks-due-pill ${task.dueDate && new Date(task.dueDate) < new Date() && task.status !== "Completed" ? "tasks-due-pill--overdue" : ""}`}>
                        {task.dueDate || "—"}
                      </span>
                    </td>
                    <td className="tasks-td">
                      <span className={`tasks-status-badge tasks-status--${task.status.toLowerCase().replace(/\s/g, "-")}`}>
                        {task.status}
                      </span>
                    </td>
                    <td className="tasks-td">
                      <select
                        className="tasks-status-select"
                        value={task.status}
                        onChange={e => updateTaskStatus(task.id, e.target.value)}
                        disabled={
                          (task.status === "Completed" && userData.role !== "superadmin") ||
                          (isManagementView && !hasManagementPermission)
                        }
                      >
                        <option value={task.status}>{task.status}</option>
                        {["Pending", "In Progress", "On Hold", "Completed"]
                          .filter(s => s !== task.status)
                          .map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    {hasManagementPermission && (
                      <td className="tasks-td tasks-actions-cell">
                        <button
                          onClick={() => setEditingTask(task)}
                          className="tasks-icon-btn tasks-icon-btn--edit"
                          title="Edit Task"
                        >
                          <FaEdit size={13} />
                        </button>
                        {userData.role === "superadmin" && (
                          <button
                            onClick={() => { if (window.confirm("Delete this task?")) deleteDoc(doc(db, "tasks", task.id)); }}
                            className="tasks-icon-btn tasks-icon-btn--delete"
                            title="Delete Task"
                          >
                            <FaTrash size={13} />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      
      {editingTask && (
        <EditTaskModal
          task={editingTask}
          onClose={() => setEditingTask(null)}
          onSave={(updated) => {
            updateDoc(doc(db, "tasks", editingTask.id), updated).then(() => {
              setEditingTask(null);
              showNotification("Task updated successfully");
            });
          }}
        />
      )}
    </div>
  );
};

export default Tasks;